import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAccountStore } from '../../entities/account';
import { useTemplateStore } from '../../entities/template';
import { usePublicationStore } from '../../entities/publication';
import { accountsApi } from '../../shared/api/accounts.api';
import { useAuthStore } from '../../shared/store/authStore';
import { templatesApi } from '../../shared/api/templates.api';
import { publicationsApi } from '../../shared/api/publications.api';
import { isSignedUrlStale } from '../../shared/lib/signedUrl';
import { AppShellSkeleton } from './AppShellSkeleton';

/**
 * Провайдер для инициализации данных приложения
 */
export function DataProvider({ children }: { children: React.ReactNode }) {
  const [isInitialized, setIsInitialized] = useState(false);
  // Защита от двойной загрузки: эффект может перезапуститься (AuthProvider дёргает
  // auth-состояние несколько раз) → грузили accounts/templates по 2 раза. Грузим
  // один раз на userId.
  const loadedForUserRef = useRef<string | null>(null);
  const setAccounts = useAccountStore((state) => state.setAccounts);
  const setAccountsLoading = useAccountStore((state) => state.setLoading);
  const setAccountsError = useAccountStore((state) => state.setError);
  
  const setTemplates = useTemplateStore((state) => state.setTemplates);
  const setTemplatesLoading = useTemplateStore((state) => state.setLoading);
  const setTemplatesError = useTemplateStore((state) => state.setError);
  
  const setPublications = usePublicationStore((state) => state.setPublications);
  const setPublicationsLoading = usePublicationStore((state) => state.setLoading);

  const userId = useAuthStore((s) => s.userId);
  const isAuthLoading = useAuthStore((s) => s.isLoading);
  const role = useAuthStore((s) => s.role);

  useEffect(() => {
    if (isAuthLoading) return;
    // Партнёру наши аккаунты, шаблоны и публикации не положены: сервер ответил
    // бы 403, а его странице они и не нужны — она грузит своё сама.
    if (!userId || role === 'partner') {
      setIsInitialized(true);
      loadedForUserRef.current = null;
      return;
    }
    // Уже грузили данные для этого юзера — пропускаем повторный запуск эффекта.
    if (loadedForUserRef.current === userId) return;
    loadedForUserRef.current = userId;
    setIsInitialized(false);

    // Одна попытка + один retry через 2 секунды: покрывает гонку auth-токена и
    // холодный старт функции. Запросы первой попытки отменяем, иначе они
    // доезжают дублями.
    async function withRetry<T>(run: (signal: AbortSignal) => Promise<T>, timeoutMs: number): Promise<T> {
      const attempt = (signal: AbortSignal) =>
        Promise.race([
          run(signal),
          new Promise<never>((_, reject) => setTimeout(() => reject(new Error('TIMEOUT')), timeoutMs)),
        ]);

      let controller = new AbortController();
      try {
        return await attempt(controller.signal);
      } catch {
        controller.abort();
        controller = new AbortController();
        await new Promise((r) => setTimeout(r, 2000));
        return attempt(controller.signal);
      }
    }

    async function loadTemplates() {
      setTemplatesLoading(true);
      try {
        const response: any = await withRetry((signal) => templatesApi.getAll(signal), 25000);
        setTemplates(
          response.map((tpl: any) => ({
            ...tpl,
            createdAt: new Date(tpl.createdAt),
            updatedAt: new Date(tpl.updatedAt),
          })),
        );
        console.log('Loaded templates from server:', response.length);
      } catch (error) {
        // Без шаблонов работает всё, кроме редактора оформления, — поэтому
        // тихо помечаем ошибку в сторе и не пугаем тостом на каждом входе.
        console.warn('Не удалось загрузить шаблоны:', error);
        setTemplatesError(error instanceof Error ? error.message : 'Failed to load templates');
      } finally {
        setTemplatesLoading(false);
      }
    }

    async function initializeData() {
      setAccountsLoading(true);

      // Шаблоны грузятся отдельно и приложение не держат: ответ тяжёлый
      // (~515kB, ~14с), а нужен он только в редакторе шаблона и формах
      // публикации. Пока он едет, интерфейс уже работает — иначе при каждом
      // возврате на вкладку телефон показывает белый экран на десяток секунд.
      loadTemplates();

      try {
        const accountsResponse: any = await withRetry((signal) => accountsApi.getAll(signal), 15000);
        
        // Преобразуем строковые даты в Date объекты
        const accounts = accountsResponse.accounts.map((acc: any) => ({
          ...acc,
          createdAt: new Date(acc.createdAt),
          updatedAt: new Date(acc.updatedAt),
          accessTokenExpiry: new Date(acc.accessTokenExpiry),
          tags: acc.tags || [],
        }));
        
        // RBAC делает сервер: он читает user_profiles под service-role, клиент — под RLS.
        // Клиентский повтор фильтра ронял стор в 0, когда профиль не прочитался и роль
        // молча падала в 'employee' с пустыми назначениями: /accounts отдавал 58,
        // в сторе оказывался ноль → «нет аккаунтов» на дашборде и в формах публикации.
        setAccounts(accounts);
        console.log('Loaded accounts from server:', accounts.length);

        // Тихо обновляем токены с остатком < 7 дней (fire-and-forget)
        const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
        const hasExpiringSoon = accounts.some((acc: any) => {
          if (!acc.isActive || !acc.accessTokenExpiry) return false;
          const msLeft = new Date(acc.accessTokenExpiry).getTime() - Date.now();
          return msLeft > 0 && msLeft < sevenDaysMs;
        });
        if (hasExpiringSoon) {
          accountsApi.autoRefresh()
            .then(async (r) => {
              if (r.refreshed > 0) {
                console.log(`✅ Auto-refreshed ${r.refreshed} token(s)`);
                // Перезагружаем аккаунты чтобы получить обновлённые токены и даты
                const updated = await accountsApi.getAll();
                const refreshedAccounts = updated.accounts.map((acc: any) => ({
                  ...acc,
                  createdAt: new Date(acc.createdAt),
                  updatedAt: new Date(acc.updatedAt),
                  accessTokenExpiry: new Date(acc.accessTokenExpiry),
                  tags: acc.tags || [],
                }));
                setAccounts(refreshedAccounts);
              }
            })
            .catch((e) => console.warn('Auto-refresh failed (non-critical):', e));
        }
        
        // Аватарки: ссылки Instagram подписаны и живут ~2 недели, после чего CDN
        // отдаёт 403 и вместо фото всюду остаются инициалы. Просим сервер
        // скопировать их к себе — постоянный адрес видят все роли. Эндпоинт
        // не админский специально: иначе сотрудники сидели бы без аватарок.
        const AVATAR_RENEW_BEFORE_MS = 3 * 24 * 60 * 60 * 1000;
        const hasStaleAvatars = accounts.some((acc: any) =>
          isSignedUrlStale(acc.profilePicture, AVATAR_RENEW_BEFORE_MS),
        );
        if (hasStaleAvatars) {
          accountsApi.mirrorAvatars()
            .then(async () => {
              const updated = await accountsApi.getAll();
              setAccounts(
                updated.accounts.map((acc: any) => ({
                  ...acc,
                  createdAt: new Date(acc.createdAt),
                  updatedAt: new Date(acc.updatedAt),
                  accessTokenExpiry: new Date(acc.accessTokenExpiry),
                  tags: acc.tags || [],
                })),
              );
              console.log('✅ Аватарки скопированы в Storage');
            })
            .catch((e) => console.warn('Копирование аватарок не удалось (некритично):', e));
        }

      } catch (error) {
        console.error('Failed to load accounts:', error);
        // Сетевые аккаунты не нужны производственным ролям (например,
        // модератору). Ошибка этого необязательного запроса не должна держать
        // весь интерфейс на каркасе навсегда. Повтор произойдёт при следующем
        // входе/обновлении страницы.
        const errorMessage = error instanceof Error ? error.message : 'Failed to load data';
        setAccountsError(errorMessage);

        if (errorMessage === 'TIMEOUT') {
          toast.error('Превышено время ожидания ответа от сервера. Проверьте подключение к сети.');
        } else {
          toast.error('Не удалось загрузить данные: ' + errorMessage);
        }
      } finally {
        setAccountsLoading(false);
        setPublicationsLoading(false);
        setIsInitialized(true);
      }
      
      // Публикации НЕ грузим здесь — PublicationsPage грузит их сам с серверной пагинацией
    }
    
    initializeData();
  }, [userId, role, isAuthLoading, setAccounts, setAccountsLoading, setAccountsError, setTemplates, setTemplatesLoading, setTemplatesError, setPublications, setPublicationsLoading]);
  
  // Показываем children только после инициализации
  // Каркас вместо спиннера: телефон выгружает вкладку из памяти, и при
  // возврате приложение поднимается заново. Пустой экран со спиннером читается
  // как «сайт перезагрузился», каркас — как «данные едут».
  // Между SIGNED_IN и запуском эффекта React успевал один раз отрисовать
  // children со сторами предыдущего пользователя. На общем компьютере это
  // на мгновение показывало чужое имя, меню и карточки. Несовпадение ref с
  // текущим userId означает именно смену сессии — держим нейтральный каркас.
  const isCurrentUserInitialized = !userId || role === 'partner' || loadedForUserRef.current === userId;
  if (!isInitialized || !isCurrentUserInitialized) {
    return <AppShellSkeleton />;
  }
  
  return <>{children}</>;
}
